import {
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import type {
  GetCarriersResponse,
  Request,
  RequestChatUnreadSummaryResponse,
  RequestListResponse,
  RequestReturnSnapshotDetail,
  RequestReturnSnapshotListItem,
} from '@magimp/shared';
import {
  fetchRequest,
  fetchRequestChatUnreadSummary,
  fetchRequestSnapshot,
  fetchRequestSnapshots,
  listCarriers,
  listChat,
  listRequests,
  type ChatListResponse,
} from '../../api/requests/index.js';
import type { RequestFilters } from './filters.js';
import { requestsKeys } from './queryKeys.js';

// WHY (#1753): the chunked chat-unread hook moved into the narrow
// `request-unread` capability so CRM My Day can consume it without a
// crm → requests feature edge. Re-exported here so existing importers of
// `queries.read.js` / the `queries.js` barrel (e.g. RequestsListPage) stay
// unchanged.
export { useRequestChatUnreadSummaries, type UnreadSummaryEntry } from './request-unread/index.js';

// -- cache-narrowing helpers ------------------------------------------

// Returns the filters object for a request LIST query key, or null if the key
// is not a concrete list query (detail/chat/summary/carriers/bare-prefix).
function requestListFiltersOf(key: readonly unknown[]): Partial<RequestFilters> | null {
  if (!Array.isArray(key) || key[0] !== 'requests') return null;
  if (key[1] === 'infinite') return (key[2] as Partial<RequestFilters> | undefined) ?? null;
  if (key.length >= 2 && typeof key[1] === 'object' && key[1] !== null) {
    return key[1] as Partial<RequestFilters>;
  }
  return null; // bare ['requests'] prefix — not a concrete list query
}

// True when a cached list query's data (single-page OR InfiniteData) contains a row with this id.
function requestListDataHasRow(data: unknown, id: string): boolean {
  type ListPage = { items?: ReadonlyArray<{ id: string }> };
  if (!data || typeof data !== 'object') return false;
  if ('pages' in data) {
    const pages = (data as InfiniteData<ListPage>).pages;
    return pages.some((p) => p.items?.some((r) => r.id === id) ?? false);
  }
  if ('items' in data) {
    return (data as ListPage).items?.some((r) => r.id === id) ?? false;
  }
  return false;
}

// INVARIANT (#1159 AC#9): on a visible→hidden transition the row
//   MUST disappear from every cached list page even before the
//   refetch returns. `invalidateQueries` only schedules a refetch;
//   the stale cache stays visible until the network round-trip
//   lands, briefly re-rendering a row the logist must no longer see.
//   This helper rewrites both single-page and `InfiniteData<>`
//   list caches under the given prefix, dropping any item whose id
//   matches. The wire `items` shape is `{ id: string }`-keyed for
//   both China (`RequestListResponse`) and Russia
//   (`RussiaRequestListResponse`); a structural row check is enough.
export function evictRowFromListCaches(
  queryClient: QueryClient,
  listPrefix: readonly unknown[],
  id: string,
): void {
  type ListPage = { items: ReadonlyArray<{ id: string }>; nextCursor: string | null };
  type Snapshot = ListPage | InfiniteData<ListPage> | undefined;
  queryClient.setQueriesData<Snapshot>({ queryKey: listPrefix }, (snapshot) => {
    if (!snapshot) return snapshot;
    if ('pages' in snapshot) {
      return {
        ...snapshot,
        pages: snapshot.pages.map((p) => ({ ...p, items: p.items.filter((r) => r.id !== id) })),
      };
    }
    if ('items' in snapshot) {
      return { ...snapshot, items: snapshot.items.filter((r) => r.id !== id) };
    }
    return snapshot;
  });
}

// Evict a deleted request from every China cache: detail, chat, and all list caches.
export function evictRequestRowEverywhere(qc: QueryClient, id: string): void {
  qc.removeQueries({ queryKey: requestsKeys.detail(id), exact: true });
  qc.removeQueries({ queryKey: requestsKeys.chat(id), exact: true });
  evictRowFromListCaches(qc, requestsKeys.all, id);
}

/**
 * @aiInvariant Narrowly invalidate caches for a single CHANGED/CREATED request:
 *   always invalidates that request's detail,
 *   invalidates only the list queries that currently CONTAIN the row
 *   (membership-loss/update), OR have `unread === true` filters
 *   (membership can be gained without the row present).
 *   When `includeSnapshots` is true, also invalidates the request's return
 *   snapshot list so a newly-created return appears in the archive UI.
 *   Never invalidates the whole ['requests'] prefix.
 */
export function narrowInvalidateForRequest(
  qc: QueryClient,
  id: string,
  includeSnapshots = false,
): void {
  void qc.invalidateQueries({ queryKey: requestsKeys.detail(id) });
  if (includeSnapshots) {
    void qc.invalidateQueries({ queryKey: requestsKeys.snapshots(id) });
  }
  void qc.invalidateQueries({
    predicate: (q) => {
      const key = q.queryKey as readonly unknown[];
      if (key[0] !== 'requests') return false;
      if (requestListDataHasRow(q.state.data, id)) return true;
      const filters = requestListFiltersOf(key);
      return filters?.unread === true;
    },
  });
}

/**
 * @aiInvariant Created requests are not present in any existing list cache yet,
 *   so row-contained narrowing cannot refresh the active list. Invalidate the
 *   created detail plus concrete request-list queries only; do not invalidate
 *   the bare ['requests'] prefix or unrelated request chat/summary caches.
 */
export function invalidateForCreatedRequest(qc: QueryClient, id: string): void {
  void qc.invalidateQueries({ queryKey: requestsKeys.detail(id) });
  void qc.invalidateQueries({
    predicate: (q) => requestListFiltersOf(q.queryKey as readonly unknown[]) !== null,
  });
}

// Narrowly invalidate ONLY the loaded `unread=true` request-list queries.
// Used by chat/read realtime events: those can flip `unread=true` list membership
// but never change the fields shown in other filters.
export function narrowInvalidateUnreadLists(qc: QueryClient): void {
  void qc.invalidateQueries({
    predicate: (q) => requestListFiltersOf(q.queryKey as readonly unknown[])?.unread === true,
  });
}

/**
 * @aiInvariant id-scoped unread-summary invalidation (#1664, PERF-10/RT-2):
 *   a chat/read realtime event names exactly ONE requestId, but the summary
 *   key embeds an id SET (`['request-chat-unread-summary', sortedIds]`) so a
 *   loaded list may hold several distinct summary queries. Invalidate only the
 *   summary queries whose key id-array CONTAINS the affected id rather than the
 *   whole prefix, so an event for one row never forces every batch to refetch.
 */
export function invalidateUnreadSummariesContaining(qc: QueryClient, requestId: string): void {
  void qc.invalidateQueries({
    predicate: (q) => {
      const key = q.queryKey as readonly unknown[];
      if (key[0] !== 'request-chat-unread-summary') return false;
      const ids = key[1];
      return Array.isArray(ids) && ids.includes(requestId);
    },
  });
}

// -- queries ----------------------------------------------------------

export function useRequestsList(
  filters: Partial<RequestFilters>,
  options: { enabled?: boolean } = {},
) {
  return useQuery<RequestListResponse>({
    queryKey: requestsKeys.list(filters),
    queryFn: ({ signal }) => listRequests(filters, { signal }),
    enabled: options.enabled ?? true,
  });
}

/**
 * @aiPurpose cursor-paginated requests list — wraps `GET /api/requests`
 *   for the load-more interaction on `RequestsListPage`. Filters minus
 *   `cursor` are part of the query key; changing them starts a fresh
 *   walk so a stale cursor never re-attaches to a different filter set.
 * @aiInvariant `getNextPageParam` returns `undefined` when the server
 *   yields `nextCursor: null`; TanStack interprets that as "no more
 *   pages" so `hasNextPage` is false and the Load-more button hides.
 */
export function useRequestsInfinite(
  filters: Partial<RequestFilters>,
  options: { enabled?: boolean } = {},
) {
  return useInfiniteQuery<
    RequestListResponse,
    Error,
    InfiniteData<RequestListResponse>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: requestsKeys.listInfinite(filters),
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) => listRequests({ ...filters, cursor: pageParam }, { signal }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: options.enabled ?? true,
  });
}

export function useRequest(id: string | null, options: { enabled?: boolean } = {}) {
  return useQuery<Request>({
    queryKey: id ? requestsKeys.detail(id) : ['request', '__none__'],
    queryFn: ({ signal }) => {
      if (!id) throw new Error('useRequest called without id');
      // WHY (#590): detail fetches always opt in to the expanded shape so
      //   the items tab / drawer can render the package breakdown without
      //   a second round-trip. List queries keep the legacy slim shape.
      // WHY (#599): same opt-in for the boxes expand so the drawer can
      //   render the read-only boxes view from the same fetch.
      // WHY (#1178): drawer is the regroup edit surface, so the detail
      //   read opts into `?includeBoxVersions=true` to receive the
      //   `RequestBox.updatedAt` token. Read-only callers (list shape)
      //   stay unaffected; the field is hidden from the row schema.
      // WHY (#1597): the drawer header renders the linked lead/deal chips,
      //   so the detail read opts into the funnel-link public codes. This
      //   key is shared by every detail writer (probe, prefetch); they all
      //   mirror the flag so a refresh never wipes the chips (AC-4).
      return fetchRequest(id, {
        signal,
        includeItemPackages: true,
        includeBoxes: true,
        includeBoxVersions: true,
        includeFunnelLinks: true,
      });
    },
    enabled: options.enabled !== false && id !== null,
  });
}

// WHY (#2140): per-request return-snapshot list for the archive tab. Mirrors
//   `useRequest`'s `enabled` guard so a transient empty id never hits the API.
export function useRequestSnapshots(requestId: string, options: { enabled?: boolean } = {}) {
  return useQuery<RequestReturnSnapshotListItem[]>({
    queryKey: requestsKeys.snapshots(requestId),
    queryFn: ({ signal }) => fetchRequestSnapshots(requestId, { signal }),
    enabled: options.enabled !== false && requestId.length > 0,
  });
}

// WHY (#2140): per-snapshot frozen detail. Stays disabled until a snapshot is
//   actually open (`snapshotId !== null`) so the archive list view does not
//   fetch a detail it has no id for.
export function useRequestSnapshot(
  requestId: string,
  snapshotId: string | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery<RequestReturnSnapshotDetail>({
    queryKey: requestsKeys.snapshot(requestId, snapshotId ?? '__none__'),
    queryFn: ({ signal }) => fetchRequestSnapshot(requestId, snapshotId as string, { signal }),
    enabled: options.enabled !== false && requestId.length > 0 && snapshotId !== null,
  });
}

export function useChatList(
  id: string | null,
  query: { cursor?: string; limit?: number } = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery<ChatListResponse>({
    queryKey: [...requestsKeys.chat(id ?? '__none__'), query],
    queryFn: () => {
      if (!id) throw new Error('useChatList called without id');
      return listChat(id, query);
    },
    enabled: options.enabled !== false && id !== null,
  });
}

/**
 * @aiPurpose batch-fetch per-request unread chat summary for the given
 *   request ids (#570). The query stays disabled until `requestIds` is
 *   non-empty so an empty list page does not hit the endpoint. Backend
 *   silently omits invisible/non-existent ids; consumers must treat a
 *   missing id in the response as `{ unreadCount: 0, isMarkedUnread: false }`.
 * @aiInvariant the queryKey is identity-stable for the same id-set (the
 *   key factory stable-sorts), so an infinite list that loads page 2
 *   does NOT invalidate the page-1 query — it simply spawns a second
 *   query for the new ids. Realtime invalidation must use the prefix
 *   `requestsKeys.chatUnreadSummaryPrefix()`.
 */
export function useRequestChatUnreadSummary(
  requestIds: readonly string[],
  options: { enabled?: boolean } = {},
) {
  return useQuery<RequestChatUnreadSummaryResponse>({
    queryKey: requestsKeys.chatUnreadSummary(requestIds),
    queryFn: ({ signal }) => fetchRequestChatUnreadSummary(requestIds, { signal }),
    enabled: (options.enabled ?? true) && requestIds.length > 0,
  });
}

export function useCarriersCatalog(options: { enabled?: boolean } = {}) {
  return useQuery<GetCarriersResponse>({
    queryKey: requestsKeys.carriersCatalog(),
    queryFn: () => listCarriers({ limit: 100 }),
    // WHY: the catalog is permission-gated by `carriers.read`. Callers pass
    // `enabled: hasPermission('carriers.read')` so a manager (lacking that
    // permission) never triggers a 403 toast at mount; the drawer falls
    // back to displaying carrierId-only.
    enabled: options.enabled !== false,
    staleTime: 5 * 60_000,
  });
}
